import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, Users, Search, Phone, ChevronRight, Loader2, LogOut, RotateCcw } from "lucide-react";
import { TandaiKeluarDialog, HapusPermanenDialog } from "../components/KeluarDialogs";
import { fmtDate } from "../lib/format";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { PageHeader, EmptyState, Field, KategoriBadge } from "../components/common";
import { PerusahaanSelect, KategoriSelect } from "../components/inputs";
import { api, errMsg } from "../lib/api";

export const AnakDialog = ({ open, onOpenChange, initial, perusahaan, onSaved }) => {
  const [f, setF] = useState({ nama: "", no_hp: "", perusahaan_id: "", aktif: true, kategori: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setF({ nama: initial?.nama || "", no_hp: initial?.no_hp || "", perusahaan_id: initial?.perusahaan_id || "", aktif: initial?.aktif ?? true, kategori: initial?.kategori || "" });
  }, [open, initial]);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.nama.trim()) return toast.error("Nama anak wajib diisi");
    setSaving(true);
    try {
      const body = { nama: f.nama.trim(), no_hp: f.no_hp.trim() || null, perusahaan_id: f.perusahaan_id || null, aktif: f.aktif, kategori: f.kategori || null };
      if (initial) await api.put(`/anak/${initial.id}`, body);
      else await api.post("/anak", body);
      toast.success(initial ? "Data anak diperbarui" : "Anak ditambahkan");
      onOpenChange(false);
      onSaved();
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="anak-dialog">
        <DialogHeader><DialogTitle className="font-heading">{initial ? "Ubah data anak" : "Tambah anak"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Nama"><Input data-testid="anak-nama-input" className="h-10" value={f.nama} onChange={(e) => setF({ ...f, nama: e.target.value })} autoFocus /></Field>
          <Field label="No. HP (opsional)"><Input data-testid="anak-nohp-input" inputMode="tel" className="h-10" value={f.no_hp} onChange={(e) => setF({ ...f, no_hp: e.target.value })} placeholder="08xxxxxxxxxx" /></Field>
          <Field label="Perusahaan">
            <PerusahaanSelect perusahaan={perusahaan} value={f.perusahaan_id} onChange={(v) => setF({ ...f, perusahaan_id: v })} allowNone testid="anak-perusahaan-select" />
          </Field>
          <Field label="Kategori (opsional)" hint="Label saja: Gold atau Platinum.">
            <KategoriSelect value={f.kategori} onChange={(v) => setF({ ...f, kategori: v })} testid="anak-kategori-select" />
          </Field>
          <label className="flex h-10 items-center justify-between rounded-lg border border-gray-200 px-4">
            <span className="text-sm font-medium">Status aktif</span>
            <Switch data-testid="anak-aktif-switch" checked={f.aktif} onCheckedChange={(v) => setF({ ...f, aktif: v })} />
          </label>
          <DialogFooter>
            <Button data-testid="anak-save-btn" type="submit" disabled={saving} className="h-10 w-full rounded-full sm:w-auto">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Simpan
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const FILTERS = [["all", "Semua"], ["true", "Aktif"], ["false", "Nonaktif"], ["keluar", "Arsip"]];

export default function AnakPage() {
  const [items, setItems] = useState(null);
  const [perusahaan, setPerusahaan] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [perId, setPerId] = useState("");
  const [kat, setKat] = useState("");
  const [dialog, setDialog] = useState({ open: false, item: null });
  const [del, setDel] = useState(null);

  const load = useCallback(() => {
    const params = { q: q || undefined, aktif: ["all", "keluar"].includes(status) ? undefined : status, keluar: status === "keluar" || undefined, perusahaan_id: perId || undefined, kategori: kat || undefined };
    return api.get("/anak", { params }).then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [q, status, perId, kat]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { api.get("/perusahaan").then((r) => setPerusahaan(r.data)); }, []);

  const [keluar, setKeluar] = useState(null);
  const restore = async (a) => {
    try { await api.post(`/anak/${a.id}/aktifkan`); toast.success(`${a.nama} diaktifkan kembali`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader title="Anak-anak" testid="anak-title" subtitle={items ? `${items.length} anak` : "Memuat…"}
        actions={<Button data-testid="anak-add-btn" className="h-10 rounded-full" onClick={() => setDialog({ open: true, item: null })}><Plus className="mr-1.5 h-4 w-4" />Tambah anak</Button>} />

      <div className="mb-3 grid gap-2 md:grid-cols-2 xl:grid-cols-[1fr_220px_180px_auto]">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input data-testid="anak-search-input" className="h-10 bg-white pl-10" placeholder="Cari nama atau no. HP…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <PerusahaanSelect perusahaan={perusahaan} value={perId} onChange={setPerId} allowAll testid="anak-filter-perusahaan" />
        <KategoriSelect value={kat} onChange={setKat} allowAll testid="anak-filter-kategori" />
        <div className="flex rounded-lg border border-gray-200 bg-white p-1">
          {FILTERS.map(([v, l]) => (
            <button key={v} data-testid={`anak-filter-status-${v}`} onClick={() => setStatus(v)}
              className={`h-10 flex-1 rounded-md px-4 text-sm font-semibold transition-colors ${status === v ? "bg-primary text-white" : "text-gray-600 hover:bg-gray-100"}`}>{l}</button>
          ))}
        </div>
      </div>

      {items && items.length === 0 && <EmptyState icon={Users} title="Belum ada anak" desc={q ? "Tidak ada yang cocok dengan pencarian." : "Tambahkan anak pertama Anda."} />}
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white empty:hidden">
        {(items || []).map((a, i) => (
          <div key={a.id} data-testid={`anak-row-${a.id}`} style={{ animationDelay: `${Math.min(i, 12) * 30}ms` }} className="fade-up flex items-center gap-2 border-b border-gray-100 px-4 py-2.5 last:border-0">
            <Link to={`/anak/${a.id}`} data-testid={`anak-open-${a.id}`} className="flex min-w-0 flex-1 items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary font-heading text-sm font-bold text-primary">{a.nama.slice(0, 2).toUpperCase()}</div>
              <div className="min-w-0">
                <p className="flex flex-wrap items-center gap-x-2 gap-y-0.5 break-words text-[15px] font-semibold text-gray-900">
                  {a.nama}
                  <KategoriBadge kategori={a.kategori} testid={`anak-kategori-${a.id}`} />
                  {a.keluar ? <span data-testid={`anak-keluar-badge-${a.id}`} className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-800">Keluar {a.tanggal_keluar ? fmtDate(a.tanggal_keluar) : ""}</span>
                    : !a.aktif && <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-gray-500">Nonaktif</span>}
                </p>
                <p className="flex flex-wrap items-center gap-x-2 text-[13px] text-gray-500">
                  {a.perusahaan_nama || "Tanpa perusahaan"}
                  {a.no_hp && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{a.no_hp}</span>}
                </p>
              </div>
            </Link>
            {a.keluar ? (
              <>
                <Button data-testid={`anak-restore-${a.id}`} variant="ghost" className="h-9 px-3 text-primary" onClick={() => restore(a)}><RotateCcw className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Aktifkan Kembali</span></Button>
              </>
            ) : (
              <>
                <Button data-testid={`anak-edit-${a.id}`} variant="ghost" size="icon" className="h-9 w-9" onClick={() => setDialog({ open: true, item: a })} aria-label="Ubah"><Pencil className="h-4 w-4" /></Button>
                <Button data-testid={`anak-keluar-${a.id}`} variant="ghost" size="icon" className="h-9 w-9 text-amber-700 hover:bg-amber-50" onClick={() => setKeluar(a)} aria-label="Tandai keluar"><LogOut className="h-4 w-4" /></Button>
              </>
            )}
            <Button data-testid={`anak-delete-${a.id}`} variant="ghost" size="icon" className="h-9 w-9 shrink-0 text-red-600 hover:bg-red-50" onClick={() => setDel(a)} aria-label="Hapus"><Trash2 className="h-4 w-4" /></Button>
            <ChevronRight className="hidden h-4 w-4 text-gray-300 sm:block" />
          </div>
        ))}
      </div>
      <AnakDialog open={dialog.open} initial={dialog.item} perusahaan={perusahaan} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))} onSaved={load} />
      <TandaiKeluarDialog anak={keluar} open={!!keluar} onOpenChange={(o) => !o && setKeluar(null)} onDone={load} />
      <HapusPermanenDialog anak={del} open={!!del} onOpenChange={(o) => !o && setDel(null)} onDone={load} />
    </div>
  );
}
